import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { formatNumber } from '@utils/usajiliFormat';

export const USAJILIPvtTrendTab: React.FC = () => {
  const { filters, refetchKey } = useUSAJILI();
  const { data, isLoading, error } = useQuery({
    queryKey: ['usajili_pvt_trend', filters, refetchKey],
    queryFn: () => usajiliApi.pvtTrend(filters),
  });

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{(error as any)?.error?.message || 'Failed to load pivot'}</Alert>;

  const headers = data?.county_headers || [];
  const n = data?.new_ids || [];
  const rep = data?.replacements || [];
  const vot = data?.voters || [];

  const columns = [
    { label: 'Sum of NEW IDS', values: n },
    { label: 'Sum of ID REPLACEMENTS', values: rep },
    { label: 'Sum of VOTERS REGISTERED', values: vot },
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        pvt trend — Counties × NEW IDS / REPLACEMENTS / VOTERS
      </Typography>
      <Paper
        sx={{
          width: '100%',
          overflow: 'auto',
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: 720 }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: 'background.default',
                  borderBottom: '2px solid #212121',
                  minWidth: 160,
                }}
              >
                Row Labels
              </TableCell>
              {columns.map((c) => (
                <TableCell
                  key={c.label}
                  align="right"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'background.default',
                    borderBottom: '2px solid #212121',
                    minWidth: 160,
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {headers.map((h, i) => {
              const isGrand = h === 'Grand Total';
              return (
                <TableRow
                  key={i}
                  sx={{
                    ...(isGrand
                      ? {
                          bgcolor: 'background.default',
                          '& td': {
                            fontWeight: 700,
                            borderTop: '2px solid #212121',
                          },
                        }
                      : {}),
                  }}
                >
                  <TableCell sx={{ fontWeight: isGrand ? 700 : 600 }}>{h}</TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.label} align="right">
                      {formatNumber(c.values[i] ?? 0)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
